import { styled, type Theme, type CSSObject } from '@mui/material';

export const PREFIX = 'BlogArticle';

export const classes = {
  root: `${PREFIX}-root`,
  title: `${PREFIX}-title`,
  detailsBlock: `${PREFIX}-detailsBlock`,
  articleLayout: `${PREFIX}-articleLayout`,
  article: `${PREFIX}-article`,
  tocSidebar: `${PREFIX}-tocSidebar`,
  textCategory: `${PREFIX}-textCategory`,
  textPubDate: `${PREFIX}-textPubDate`,
  articleTextContainer: `${PREFIX}-articleTextContainer`,
  articleImage: `${PREFIX}-articleImage`,
  articleImageContain: `${PREFIX}-articleImageContain`,
  authorElement: `${PREFIX}-authorElement`,
  richTextContainer: `${PREFIX}-richTextContainer`,
};

export const StyledBlogArticle = styled('div')(
  ({ theme }: { theme: Theme }) => ({
    color: theme.palette.common.white,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    margin: '1rem auto',
    paddingInline: '24px',

    [`& .${classes.title}`]: {
      ...(theme.mixins.neutralGradient as CSSObject),
      backgroundClip: 'text',
      WebkitBackgroundClip: 'text',
      WebkitTextFillColor: 'transparent',
      marginRight: 'auto',
      position: 'relative !important' as 'relative',
      fontFamily: 'ClashDisplay',
      fontWeight: 'bold',
      marginBottom: '32px',
      marginTop: '40px',
      fontSize: '3.2rem',
      lineHeight: '1.2',
      letterSpacing: '-0.02em',

      [theme.breakpoints.down('md')]: {
        fontSize: '2.5rem',
        lineHeight: '1.2',
      },

      [theme.breakpoints.down('sm')]: {
        fontSize: '2rem',
        padding: '0',
        lineHeight: '1.15',
      },
    },

    [`& .${classes.articleLayout}`]: {
      display: 'flex',
      flexDirection: 'row',
      gap: '32px',
      alignItems: 'flex-start',
      width: '100%',
      maxWidth: '1400px',
      marginTop: '100px',
      marginLeft: 'auto',
      marginRight: 'auto',

      [theme.breakpoints.down('sm')]: {
        marginTop: '0px',
      },
    },

    [`& .${classes.tocSidebar}`]: {
      display: 'none',
      width: '220px',
      flexShrink: 0,
      position: 'sticky',
      top: '100px',

      [theme.breakpoints.up('lg')]: {
        display: 'block',
      },
    },

    [`& .${classes.article}`]: {
      flex: 1,
      minWidth: 0,
      textAlign: 'left !important' as 'left',
      borderRadius: '20px',
      overflow: 'hidden',
      background: 'rgba(255, 255, 255, 0.04)',
      border: '1px solid rgba(255, 255, 255, 0.08)',
      boxShadow:
        '0 0 0 1px rgba(255, 255, 255, 0.06), 0 0 24px 4px rgba(255, 255, 255, 0.04)',
    },

    [`& .${classes.textCategory}`]: {
      textAlign: 'left',
      fontSize: '14px',
      color: theme.palette.secondary.light,
      fontWeight: 'bold',
      padding: '0 0 36px',
    },

    [`& .${classes.textPubDate}`]: {
      textAlign: 'left',
      fontSize: '14px',
      color: theme.palette.secondary.light,
    },

    [`& .${classes.articleTextContainer}`]: {
      padding: '24px',
      '& > h2': {
        boxSizing: 'border-box',
        width: '100%',
      },
    },

    [`& .${classes.articleImage}`]: {
      objectFit: 'cover',
      width: '100%',
      maxHeight: '406px',
    },

    [`& .${classes.articleImageContain}`]: {
      objectFit: 'contain',
    },

    [`& .${classes.authorElement}`]: {
      textAlign: 'right',
      padding: '8px 0',
    },

    [`& .${classes.richTextContainer}`]: {
      width: '100%',
      maxWidth: '72ch',
      marginLeft: 'auto',
      marginRight: 'auto',
      paddingBottom: '48px',

      '& div': {
        padding: '0',
      },

      '& *': {
        color: theme.palette.common.white,
      },

      '& p': {
        fontSize: '1.05rem',
        lineHeight: '1.85',
        color: 'rgba(255,255,255,0.82)',
        margin: '0 0 1.5rem 0',
      },

      '& h2': {
        fontFamily: 'ClashDisplay',
        fontSize: '2rem',
        fontWeight: 700,
        letterSpacing: '-0.02em',
        lineHeight: '1.25',
        margin: '3.5rem 0 1rem',
        color: theme.palette.common.white,

        [theme.breakpoints.down('sm')]: {
          fontSize: '1.6rem',
          margin: '2.5rem 0 0.75rem',
        },
      },

      '& h3': {
        fontFamily: 'ClashDisplay',
        fontSize: '1.4rem',
        fontWeight: 600,
        letterSpacing: '-0.01em',
        lineHeight: '1.3',
        margin: '2.5rem 0 0.75rem',
        color: theme.palette.common.white,

        [theme.breakpoints.down('sm')]: {
          fontSize: '1.2rem',
        },
      },

      '& ul, & ol': {
        color: 'rgba(255,255,255,0.82)',
        margin: '0 0 1.5rem 0',
        paddingLeft: '1.5rem',
      },

      '& li': {
        fontSize: '1.05rem',
        lineHeight: '1.85',
        marginBottom: '0.4rem',
      },

      '& blockquote': {
        margin: '2.5rem 0',
      },

      '& pre': {
        margin: '0 0 1.5rem 0',
      },
    },
  })
);

export const blogArticleImageHeight = 306;
